/**
 * Mention — `@username` node. Built on `@tiptap/extension-mention` +
 * `@tiptap/suggestion`. The suggestion popover is provided by
 * `components/notebook/MentionList.tsx` (a cmdk palette over
 * `useProfiles`).
 *
 * Markdown form: `@Display Name` (the label, since the userId is opaque).
 * Phase 5 (comments) is what actually fires notifications on mention;
 * Phase 3 only renders the chip.
 */

import Mention from '@tiptap/extension-mention'
import { Suggestion, type SuggestionOptions } from '@tiptap/suggestion'
import { ReactRenderer } from '@tiptap/react'
import { PluginKey } from '@tiptap/pm/state'
import { mountSuggestion, unmountSuggestion } from './suggestionRenderer'
import { MentionList, type MentionListRef } from '@/components/notebook/MentionList'

/**
 * Override of the base `Mention` extension's `addProseMirrorPlugins` —
 * we replace its default Suggestion with a cmdk-backed palette, so we
 * MUST give it a unique pluginKey. (See the note in slashExtension.ts
 * for why this can't be the shared `SuggestionPluginKey`.)
 */
const MentionPalettePluginKey = new PluginKey('mention-palette')


export interface MentionItem {
  id: string
  label: string
}

export interface MentionOptions {
  HTMLAttributes: Record<string, unknown>
  /** Called for every keystroke; returns the candidates to show in the palette. */
  fetchItems: (query: string) => MentionItem[] | Promise<MentionItem[]>
}

export const MentionWithPalette = Mention.extend<MentionOptions>({
  name: 'mention',

  addOptions() {
    return {
      HTMLAttributes: { class: 'mention-chip' },
      fetchItems: () => [],
    }
  },

  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const label = node.attrs.label || node.attrs.id
          state.write(`@${label}`)
        },
      },
    }
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-id'),
        renderHTML: (attrs) => (attrs.id ? { 'data-id': attrs.id } : {}),
      },
      label: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-label'),
        renderHTML: (attrs) => (attrs.label ? { 'data-label': attrs.label } : {}),
      },
    }
  },

  renderText({ node }) {
    return `@${node.attrs.label ?? node.attrs.id ?? ''}`
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      {
        ...HTMLAttributes,
        'data-id': node.attrs.id,
        'data-label': node.attrs.label,
        'data-mention': '',
      },
      `@${node.attrs.label ?? node.attrs.id ?? ''}`,
    ]
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        pluginKey: MentionPalettePluginKey,
        char: '@',
        allowSpaces: false,
        items: async ({ query }) => {
          const items = await this.options.fetchItems(query)
          return items.slice(0, 8)
        },
        command: ({ editor, range, props }) => {
          // props = the chosen MentionItem
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: 'mention',
                attrs: { id: props.id, label: props.label },
              },
              { type: 'text', text: ' ' },
            ])
            .run()
        },
        render: () => {
          let component: ReactRenderer<MentionListRef> | null = null
          return {
            onStart: (props) => {
              component = new ReactRenderer(MentionList, {
                props,
                editor: props.editor,
              })
              mountSuggestion(component)
            },
            onUpdate: (props) => {
              component?.updateProps(props)
            },
            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                return true
              }
              return component?.ref?.onKeyDown(props) ?? false
            },
            onExit: () => {
              unmountSuggestion(component)
              component = null
            },
          }
        },
      }),
    ]
  },
})
